import { createRoot } from 'react-dom/client';
import React, { useEffect, useState, useCallback, useRef, RefObject } from 'react';
import Navbar from './Navbar.jsx';
import Contact from 'computing-den-lib/dist/client/Contact.jsx';
import Intro from './Intro.jsx';
import {
  useRestoreScroll,
  useGoToHash,
  useDragScroll,
  useClickWithoutDrag,
  getBookHref,
} from 'computing-den-lib/dist/client/misc.jsx';
import * as I from 'computing-den-lib/dist/client/Icon.jsx';
import { ImageCarousel, ImageCarouselScrollButtons, ImageCarouselScrollIndicator } from './ImageCarousel.jsx';
import IconCardGrid, { type IconCardData, IconCard } from './IconCardGrid.jsx';
import type { GalleryImageData } from './types.js';
// import Hello from 'computing-den-lib/client/Hello.jsx';

window.onload = function setup() {
  const root = createRoot(document.getElementById('app')!);
  root.render(<Home hash={window.location.hash} />);
};

function Home(props: { hash: string }) {
  useRestoreScroll();
  useGoToHash(props.hash);

  return (
    <>
      <Navbar transparentAtTop />
      <Intro />
      <Services />
      <Portfolio />
      <About />
      {/*<Pricing />*/}
      <Testimonials />
      <Tech />
      <Contact forHomePage />
    </>
  );
}

function Services() {
  return (
    <section id="services">
      <div className="content">
        <h3 className="section-subheading">OUR SERVICES</h3>
        <h1 className="section-heading">How Can We Help You?</h1>
        <IconCardGrid blue>
          <IconCard heading="Consultation & Brainstorming" icon={<I.IconComments />}>
            <IconCard.Desc>
              Collaborate with seasoned professionals to refine project ideas and create robust, scalable SaaS
              applications tailored to your business needs.
            </IconCard.Desc>
          </IconCard>
          <IconCard heading="Custom Web Development & UX/UI" icon={<I.IconCode />}>
            <IconCard.Desc>
              Develop complex, robust web applications tailored to your unique requirements.
            </IconCard.Desc>
          </IconCard>
          <IconCard heading="Enterprise Automation" icon={<I.IconLightning />}>
            <IconCard.Desc>
              Streamline your enterprise operations with cutting-edge automation technologies, from custom workflows to
              integrated systems, enhancing productivity and reducing manual tasks.
            </IconCard.Desc>
          </IconCard>
          <IconCard heading="Legacy Software Modernization" icon={<I.IconRecycle />}>
            <IconCard.Desc>
              Revitalize outdated software and develop custom solutions like CMS and CRM to automate and enhance
              business operations.
            </IconCard.Desc>
          </IconCard>
          <IconCard heading="Hosting & Managed Cloud" icon={<I.IconCloud />}>
            <IconCard.Desc>
              Ensure smooth and efficient deployment of your applications, with expert-managed cloud services to
              guarantee performance, scalability, and security.
            </IconCard.Desc>
          </IconCard>
          <IconCard heading="Long-Term Support" icon={<I.IconShield />}>
            <IconCard.Desc>
              Maintain high-quality standards, safeguard your data, and benefit from dedicated long-term support to keep
              your systems running smoothly.
            </IconCard.Desc>
          </IconCard>
        </IconCardGrid>
        {/*
          <p>Schedule a consultation to discuss your needs and brainstorm solutions we can build for you.</p>
          <div className="button-container">
            <a className="book-button" href={getBookHref()}>
              SCHEDULE CONSULTATION
            </a>
          </div>
          */}
      </div>
    </section>
  );
}

function Tech() {
  return (
    <section id="tech">
      <div className="content">
        <h3 className="section-subheading">EXPERTS IN TECH</h3>
        <h1 className="section-heading">Our Tech Stack</h1>
        <div className="grid">
          <div className="tech-stack-item">
            <I.IconTypeScript />
            <p>TypeScript</p>
          </div>
          <div className="tech-stack-item">
            <I.IconReact />
            <p>React.js</p>
          </div>
          <div className="tech-stack-item">
            <I.IconNode />
            <p>Node.js</p>
          </div>
          <div className="tech-stack-item">
            <I.IconSql />
            <p>PostgreSQL</p>
          </div>
          <div className="tech-stack-item">
            <I.IconNginx />
            <p>Nginx</p>
          </div>
          <div className="tech-stack-item">
            <I.IconLinux />
            <p>Linux</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function Portfolio() {
  // const windowInnerWidth = useWindowInnerWidth();

  const dragAreaRef = useRef<HTMLElement>(null);
  const scrollArea = useRef<HTMLElement>(null);

  return (
    <section id="portfolio" ref={dragAreaRef}>
      <div className="heading-content">
        <h3 className="section-subheading">PORTFOLIO</h3>
        <div className="section-heading-and-buttons">
          <h1 className="section-heading">Success Stories</h1>
          <ImageCarouselScrollButtons scrollArea={scrollArea} />
        </div>
      </div>
      <ImageCarouselScrollIndicator scrollArea={scrollArea} />
      <ImageCarousel images={PORTFOLIO_IMAGES} dragArea={dragAreaRef} scrollArea={scrollArea} />
    </section>
  );
}

function About() {
  return (
    <section id="about">
      <div className="content">
        <h3 className="section-subheading">ABOUT US</h3>
        <h1 className="section-heading">Who Are We?</h1>
        <div className="body">
          <figure className="sean">
            <a href="https://www.linkedin.com/in/sean-shirazi" target="_blank">
              <img src="/shahab-sean-shirazi-small.jpg" alt="Shahab (Sean) Shirazi" width="250px" height="377px" />
            </a>
            <figcaption>
              <a href="https://www.linkedin.com/in/sean-shirazi" target="_blank" className="styled">
                <span className="name">Shahab (Sean) Shirazi</span>, Founder
              </a>
            </figcaption>
          </figure>

          <p>Our core mission is to build high-quality custom software for your business.</p>

          <p>
            Computing Den was founded in Turkey in 2022 by Shahab (Sean) Shirazi, following a decade-long career as a{' '}
            full-stack web developer.
          </p>

          <p>
            To bring your projects and visions to life, we build a small, focused team of highly experienced and
            professional programmers and designers from around the world.
          </p>

          <p>
            Our team, ensures the highest quality of code and design, as well as a robust foundational architecture to
            build upon for years. Our team is led by Sean who oversees every project, providing meticulous oversight to
            guarantee excellence. Our commitment to quality is further reinforced by rigorous automated testing
            throughout the development process.
          </p>
          <div className="clear" />
        </div>
      </div>
    </section>
  );
}

function Pricing() {
  return (
    <section id="pricing">
      <div className="intro-content">
        <h3 className="section-subheading">PRICING</h3>
        <h1 className="section-heading">Transparent Pricing</h1>
        <div className="body">
          <IconCardGrid green>
            <IconCard heading="Consultation" icon={<I.IconComments />}>
              <IconCard.Desc>
                Connect with an expert to discuss your current solutions and challenges, brainstorm and develop new
                strategies for your business, and get a cost estimate.
              </IconCard.Desc>
              <IconCard.Footer>€100 per hour, the first hour is free.</IconCard.Footer>
            </IconCard>
            <IconCard heading="Hosting & Cloud Management" icon={<I.IconCloud />}>
              <IconCard.Desc>
                We handle everything: deployment, maintenance, security updates, 24/7 monitoring, and emergency
                response.
              </IconCard.Desc>
              <IconCard.Footer>
                €100-€500 per month for websites with fewer than one million requests per month.
              </IconCard.Footer>
            </IconCard>
            <IconCard heading="Bug Fixes" icon={<I.IconFix />}>
              <IconCard.Desc>
                We're committed to quality: any bugs or issues you encounter will be fixed promptly and at no extra
                cost, keeping your systems running smoothly.
              </IconCard.Desc>
              <IconCard.Footer>No extra charge.</IconCard.Footer>
            </IconCard>
            <IconCard heading="Self-Hosting" icon={<I.IconAdmin />}>
              <IconCard.Desc>
                We provide the complete source code, enabling you to securely host the software within your own
                infrastructure while maintaining full data sovereignty.
              </IconCard.Desc>
              <IconCard.Footer>No extra charge.</IconCard.Footer>
            </IconCard>
          </IconCardGrid>
        </div>
      </div>
      <CaseStudy title="Pricing Case Studies" images={CASE_STUDIES} />
    </section>
  );
}

function CaseStudy(props: { title: string; images: GalleryImageData[] }) {
  const dragAreaRef = useRef<HTMLElement>(null);
  const scrollArea = useRef<HTMLElement>(null);
  return (
    <div className="case-study" ref={dragAreaRef as RefObject<HTMLDivElement>}>
      <div className="heading-and-buttons">
        <h2>{props.title}</h2>
        <ImageCarouselScrollButtons scrollArea={scrollArea} />
      </div>
      <ImageCarouselScrollIndicator scrollArea={scrollArea} />
      <ImageCarousel images={props.images} dragArea={dragAreaRef} scrollArea={scrollArea} />
    </div>
  );
}

function Testimonials() {
  return (
    <section id="testimonials">
      <div className="content">
        <h3 className="section-subheading">TESTIMONIALS</h3>
        <h1 className="section-heading">What Do Our Clients Say?</h1>
        <div className="body">
          <div className="testimonial">
            <img src="/michael-ringkvist.jpeg" />
            <I.IconQuote />
            <div className="body">
              <p>
                I have had the pleasure of working with Sean for the past seven years and have consistently been
                impressed by his exceptional skills, depth of knowledge, and dedication to his craft.
              </p>
              <div className="footer">
                <p className="name">Michael Ringkvist</p>
                <p className="title">
                  CEO of Intecredo International Aps, chairman of the board of Danske Inkasso Aps, 2023
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const PORTFOLIO_IMAGES: GalleryImageData[] = [
  {
    src: '/portfolio/portals.png',
    caption: (
      <>
        <h2>Internal system for Intecredo International ApS</h2>
        <ul className="styled">
          <li>Custom CRM</li>
          <li>Human Resource Management System (HRMS)</li>
          <li>Document, Email, and Inventory management</li>
          <li>
            300K lines of code since <b>2016</b>
          </li>
        </ul>
      </>
    ),
  },
  {
    src: '/portfolio/chat-video.png',
    caption: (
      <>
        <h2>Audio/Video chat</h2>
        <ul className="styled">
          <li>Real-time audio/video streams over WebRTC</li>
          <li>Real-time text messages over WebSocket</li>
          <li>Screen sharing capabilities</li>
        </ul>
      </>
    ),
  },
  // {
  //   src: "/portfolio/chat.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  {
    src: '/portfolio/document-portal-search.png',
    caption: (
      <>
        <h2>Document management</h2>
        <ul className="styled">
          <li>Manage millions of internal PDFs</li>
          <li>Fast search by content, tags etc</li>
          <li>Integrated PDF and image viewers</li>
          <li>Multilingual OCR and text extraction from PDFs</li>
          <li>RESTful APIs</li>
        </ul>
      </>
    ),
  },
  // {
  //   src: "/portfolio/document-portal-sidebar.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  // {
  //   src: "/portfolio/document-portal-pdf-preview.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  // {
  //   src: "/portfolio/workflow-link.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  {
    src: '/portfolio/workflow-params.png',
    caption: (
      <>
        <h2>Workflow creator</h2>
        <ul className="styled">
          <li>Managing complex workflows</li>
          <li>Visualize and automate complex business logic</li>
        </ul>
      </>
    ),
  },
  {
    src: '/portfolio/email-portal-list.png',
    caption: (
      <>
        <h2>Email management</h2>
        <ul className="styled">
          <li>Automatic handling of requests based on predefined email templates</li>
          <li>Search emails by contents and attachments</li>
          <li>Integrated with the document management system</li>
        </ul>
      </>
    ),
  },
  // {
  //   src: "/portfolio/email-portal-pdf-preview.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  // {
  //   src: "/portfolio/email-portal-right-sidebar.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  // {
  //   src: "/portfolio/event-disc.png",
  //   caption:
  //     "Our core mission is to automate your internal business processes.",
  // },
  {
    src: '/portfolio/event-disc-sidebar.png',
    caption: (
      <>
        <h2>Circular calendar</h2>
        <ul className="styled">
          <li>A creative way to manage your busy schedule</li>
          <li>Zoom in for nested calendars</li>
        </ul>
      </>
    ),
  },
  {
    src: '/portfolio/keyboard-shortcuts.png',
    caption: (
      <>
        <h2>Extensive keyboard shortcuts to speed up workflows</h2>
        <ul className="styled">
          <li>Fast navigation throughout portals</li>
        </ul>
      </>
    ),
  },
  {
    src: '/portfolio/case-portal-customizable-layout.png',
    caption: (
      <>
        <h2>Customizable page layouts for each user</h2>
        <ul className="styled">
          <li>Drag & drop widgets</li>
          <li>Custom event timelines</li>
        </ul>
      </>
    ),
  },
  {
    src: '/portfolio/case-portal-more-actions.png',
    caption: (
      <>
        <h2>And a lot more ...</h2>
        <ul className="styled">
          <li>Integrated with Asterisk for VoIP Phonecalls</li>
          <li>Create notes, assignments, payment agreements etc.</li>
          <li>Timeline of events</li>
        </ul>
      </>
    ),
  },
  {
    src: '/portfolio/unforget-screenshots.png',
    withoutShadow: true,
    caption: (
      <>
        <h2>Unforget: minimalist note taking app</h2>
        <ul className="styled">
          <li>
            <a href="https://news.ycombinator.com/item?id=40645743" target="_blank" className="styled">
              <b>Featured on Hacker News</b>
            </a>
          </li>
          <li>End-to-end encrypted</li>
          <li>Desktop, mobile, and web</li>
          <li>Self-hosted and cloud options</li>
          <li>Progressive Web App (PWA)</li>
          <li>
            <a href="https://github.com/computing-den/unforget" target="_blank" className="styled">
              Open source
            </a>{' '}
            MIT License
          </li>
          <li>
            <a href="https://unforget.computing-den.com/demo" target="_blank" className="styled">
              Try it now
            </a>
          </li>
        </ul>
      </>
    ),
  },
];

// const CASE_STUDY_IMAGES_ARCHITECTURE_STUDIO: GalleryImageData[] = [
//   {
//     src: '/case-studies/gallery/gallery-home.jpg',
//     caption: (
//       <>
//         <h2>Photo Gallery</h2>
//         <ul className="styled">
//           <li>Custom CRM</li>
//           <li>Human Resource Management System (HRMS)</li>
//           <li>Document, Email, and Inventory management</li>
//           <li>
//             300K lines of code since <b>2016</b>
//           </li>
//         </ul>
//       </>
//     ),
//   },
//   {
//     src: '/case-studies/gallery/gallery-search-completion.jpg',
//     caption: (
//       <>
//         <h2>Photo Gallery</h2>
//         <ul className="styled">
//           <li>Custom CRM</li>
//           <li>Human Resource Management System (HRMS)</li>
//           <li>Document, Email, and Inventory management</li>
//           <li>
//             300K lines of code since <b>2016</b>
//           </li>
//         </ul>
//       </>
//     ),
//   },
// ];

const CASE_STUDIES: GalleryImageData[] = [
  {
    src: '/case-studies/others/landmark-realty.jpg',
    caption: (
      <>
        <h2>Landmark Realty, 2024</h2>
        <ul className="styled">
          <li>Post real estate listings</li>
          <li>Advanced property search</li>
          <li>Display popular listings</li>
          <li>Interactive map view</li>
          <li>Mobile-responsive design</li>
          <li>Private messaging</li>
          <li>Real-time notifications</li>
          <li>
            <b>3 developers • 1 month • €15K</b>
          </li>
        </ul>
      </>
    ),
  },
  {
    src: '/case-studies/others/postcard.jpg',
    caption: (
      <>
        <h2>First Impression, 2024</h2>
        <ul className="styled">
          <li>Custom postcard design</li>
          <li>Template library</li>
          <li>Intuitive editor</li>
          <li>Customizable QR codes on postcards</li>
          <li>Secure payment gateway integration</li>
          <li>Online order tracking</li>
          <li>
            <b>2 developers • 1 designer • 2 months • €26K</b>
          </li>
        </ul>
      </>
    ),
  },
  {
    src: '/case-studies/gallery/gallery-edit.jpg',
    caption: (
      <>
        <h2>Architecture Studio, 2022</h2>
        <ul className="styled">
          <li>Manage hundreds of thousands of images</li>
          <li>Efficient search capabilities</li>
          <li>Similar image suggestions</li>
          <li>Storage and automatic backups using SQLite</li>
          <li>
            <b>1 developer • 3 weeks • €4K</b>
          </li>
        </ul>
      </>
    ),
  },
  {
    src: '/case-studies/others/fileforte-1-cropped.png',
    caption: (
      <>
        <h2>FileForte, 2023</h2>
        <ul className="styled">
          <li>Scan and index thousands of files on disk</li>
          <li>Extract text from PDF files</li>
          <li>Instant search capabilities</li>
          <li>Convenient tagging with auto suggestions based on content</li>
          <li>Calculate SHA1 hash and find duplicate files</li>
          <li>Desktop app using Electron.js</li>
          <li>
            <b>1 developer • 5 weeks • €6K</b>
          </li>
        </ul>
      </>
    ),
  },
];
